import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { LoadingComponent } from '@blaze-cms/react-page-builder';
import { useTranslation } from '@blaze-cms/nextjs-components';
import parseHTML from 'html-react-parser';
import Modal from '@blaze-react/modal';
import FormInput from '../FormInput';
import { CREATE_USER, CHECK_USER } from '../../services/mutations';
import { buildAddressInput, validatePassword } from './helpers';
import { INIT_FORM_STATE, CLASSNAME } from './constants';
import { validateForm } from '../helpers';
import { GET_INDUSTRIES, GET_COUNTIES_BY_COUNTRY_ID, GET_COUNTRIES } from '../../services/querys';
import buildSelectOptions from '../helpers/build-select-options';
import buildTitleOptions from '../helpers/build-title-options';
import { TRANSLATION_NAMESPACE } from '../../constants';

const RegistrationForm = ({ modifier, children, privacyPolicyUrl, termsAndConditionsUrl }) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACE);
  const [formValues, setFormValues] = useState(INIT_FORM_STATE);
  const [countryId, setCoutryId] = useState('');
  const [showSubmitButton, setShowSubmitButton] = useState(true);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showSuccessMessage, setShowSuccesMessage] = useState(false);
  const [emailChecked, setEmailChecked] = useState(false);
  const [regionRedirectMessage, setRegionRedirectMessage] = useState(null);
  const formRef = useRef(null);

  const { data: { industries = [] } = {} } = useQuery(GET_INDUSTRIES);
  const { data: { countries = [] } = {}, loading: countriesLoading } = useQuery(GET_COUNTRIES);
  const { data: { counties = [] } = {} } = useQuery(GET_COUNTIES_BY_COUNTRY_ID, {
    variables: { countryId },
    skip: countriesLoading
  });

  const [createUser, { loading }] = useMutation(CREATE_USER, {
    onCompleted: () => {
      setShowSubmitButton(false);
      setShowErrorMessage(false);
      setShowSuccesMessage(true);
    },
    onError: error => {
      setShowErrorMessage(error.message);
      setShowSuccesMessage(false);
    }
  });

  const [checkUser, { loading: checkUserLoading }] = useMutation(CHECK_USER, {
    onCompleted: () => {
      setEmailChecked(true);
    },
    onError: error => {
      const invalidRegionError = error.graphQLErrors.find(
        err => err.extensions.exception.code === 'invalidRegion'
      );

      if (invalidRegionError) return setRegionRedirectMessage(error.message);
      setShowErrorMessage(error.message);
    }
  });

  const handleValueChange = (key, value) => {
    const parsedValue = key === 'tocAccepted' ? value.checked : value;
    const updatedValues = { ...formValues };

    if (key === 'password') {
      const isValid = validatePassword(value);

      updatedValues.password = {
        ...updatedValues.password,
        error: !isValid
      };
    }

    if (key === 'passwordConfirm') {
      updatedValues.passwordConfirm = {
        ...updatedValues.passwordConfirm,
        error: value !== updatedValues.password.value
      };
    }

    if (key === 'country') {
      const { id: newCountryId } = countries.find(({ code }) => code === value);
      updatedValues.county = {
        ...updatedValues.county,
        value: ''
      };
      setCoutryId(newCountryId);
    }
    if (key === 'county') {
      const { name: cName } = counties.find(({ code }) => code === value);

      updatedValues.countyName = {
        ...updatedValues.countyName,
        value: cName
      };
    }
    setFormValues({
      ...updatedValues,
      [key]: { ...formValues[key], value: parsedValue, error: updatedValues[key].error }
    });
  };

  const handleRegister = () => {
    if (loading) return;

    createUser({
      variables: {
        input: buildAddressInput(formValues)
      }
    });
  };

  const handleFormValidation = validatedFormData => {
    setFormValues({ ...formValues, ...validatedFormData });
    setTimeout(() => {
      const firstErrorElement = formRef.current.querySelector('.error');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstErrorElement.focus();
      }
    }, 500);
  };

  const handleSubmit = () => {
    const [isValid, validatedFormData] = validateForm(formValues);
    if (isValid) handleRegister();
    else {
      handleFormValidation(validatedFormData);
    }
  };

  const handleUserCheck = () => {
    const [, validatedFormData] = validateForm({ email: formValues.email });
    const { email } = validatedFormData;
    if (!email.error) {
      checkUser({
        variables: {
          input: {
            email: formValues.email.value,
            country: formValues.country.value
          }
        }
      });
    } else {
      handleFormValidation({ email });
    }
  };

  const parsedModifier = modifier ? ` ${modifier}` : '';
  const formClassName = `pp-user-form${parsedModifier}`;
  const parsedIndustries = buildSelectOptions(industries, t);
  const parsedCountries = buildSelectOptions(countries, t);
  const parsedCounties = buildSelectOptions(counties, t);

  const handleSubmitForm = e => {
    e.preventDefault();
    if (emailChecked) {
      handleSubmit();
    } else {
      handleUserCheck();
    }
  };

  return (
    <>
      {regionRedirectMessage && (
        <Modal
          showFooter={false}
          isSimple
          onClose={() => setRegionRedirectMessage(null)}
          className="redirect-modal">
          {parseHTML(regionRedirectMessage)}
        </Modal>
      )}
      {!showSuccessMessage && (
        <form onSubmit={handleSubmitForm} className={`${formClassName} ${CLASSNAME}`} ref={formRef}>
          <span className="pp-user-form__sub_header">{t('YOUR_LOGIN')}</span>
          {!emailChecked && (
            <>
              <div className="pp-user-form__input-group">
                <FormInput
                  id="country"
                  className="pp-user-form__input-group"
                  label={t('COUNTRY')}
                  data={formValues.country}
                  type="select"
                  options={parsedCountries}
                  isRequired
                  onChange={handleValueChange}
                />
              </div>
            </>
          )}
          <div className="pp-user-form__input-group">
            <FormInput
              id="email"
              className="pp-user-form__input-group"
              label={t('EMAIL')}
              data={formValues.email}
              onChange={handleValueChange}
              type="text"
              isRequired
            />
          </div>

          {!emailChecked && (
            <>
              <button
                className="pp-user-form__submit-button"
                type="submit"
                disabled={checkUserLoading}>
                {checkUserLoading || loading ? <LoadingComponent /> : t('CONTINUE')}
              </button>
            </>
          )}
          {emailChecked && (
            <>
              <div className="pp-user-form__input-group">
                <FormInput
                  id="password"
                  className="pp-user-form__input-group"
                  label={t('PASSWORD')}
                  data={formValues.password}
                  onChange={handleValueChange}
                  type="password"
                  isRequired
                />
              </div>
              <div className="pp-user-form__input-group">
                <FormInput
                  id="passwordConfirm"
                  className="pp-user-form__input-group"
                  label={t('CONFIRM_PASSWORD')}
                  data={formValues.passwordConfirm}
                  onChange={handleValueChange}
                  type="password"
                  isRequired
                />
              </div>
              <span className="pp-user-form__sub_header">{t('YOUR_DETAILS')}</span>
              <div className="pp-user-form__input-group">
                <FormInput
                  id="title"
                  className="pp-user-form__input-group"
                  label={t('TITLE')}
                  data={formValues.title}
                  type="select"
                  options={buildTitleOptions(t)}
                  isRequired
                  onChange={handleValueChange}
                />
                <FormInput
                  id="firstname"
                  className="pp-user-form__input-group"
                  label={t('FIRST_NAME')}
                  data={formValues.firstname}
                  isRequired
                  type="text"
                  onChange={handleValueChange}
                />
                <FormInput
                  id="lastname"
                  className="pp-user-form__input-group"
                  label={t('LAST_NAME')}
                  data={formValues.lastname}
                  onChange={handleValueChange}
                  type="text"
                  isRequired
                />
              </div>
              <div className="pp-user-form__input-group">
                <FormInput
                  id="jobTitle"
                  className="pp-user-form__input-group"
                  label={t('JOB_TITLE')}
                  data={formValues.jobTitle}
                  onChange={handleValueChange}
                  type="text"
                  isRequired
                />
              </div>
              <div className="pp-user-form__input-group">
                <FormInput
                  id="company"
                  className="pp-user-form__input-group"
                  label={t('COMPANY_NAME')}
                  data={formValues.company}
                  onChange={handleValueChange}
                  type="text"
                  isRequired
                />
              </div>
              <div className="pp-user-form__input-group">
                <FormInput
                  id="tel"
                  className="pp-user-form__input-group"
                  label={t('TELEPHONE')}
                  data={formValues.tel}
                  onChange={handleValueChange}
                  type="text"
                  isRequired
                />
              </div>
              <div className="pp-user-form__input-group">
                <FormInput
                  id="industry"
                  className="pp-user-form__input-group"
                  label={t('INDUSTRY')}
                  data={formValues.industry}
                  type="select"
                  options={parsedIndustries}
                  isRequired
                  onChange={handleValueChange}
                />
              </div>
              <span className="pp-user-form__sub_header">{t('BILLING_ADDRESS')}</span>
              <div className="pp-user-form__input-group">
                <FormInput
                  id="address1"
                  className="pp-user-form__input-group"
                  label={t('ADDRESS_LINE_1')}
                  data={formValues.address1}
                  type="text"
                  isRequired
                  onChange={handleValueChange}
                />
              </div>
              <div className="pp-user-form__input-group">
                <FormInput
                  id="address2"
                  className="pp-user-form__input-group"
                  label={t('ADDRESS_LINE_2')}
                  data={formValues.address2}
                  type="text"
                  onChange={handleValueChange}
                />
              </div>
              <div className="pp-user-form__input-group">
                <FormInput
                  id="city"
                  className="pp-user-form__input-group"
                  label={t('CITY_TOWN')}
                  data={formValues.city}
                  type="text"
                  isRequired
                  onChange={handleValueChange}
                />
                <FormInput
                  id="postcode"
                  className="pp-user-form__input-group"
                  label={t('POSTCODE')}
                  data={formValues.postcode}
                  type="text"
                  isRequired
                  onChange={handleValueChange}
                />
              </div>
              <div className="pp-user-form__input-group">
                <FormInput
                  id="country"
                  className="pp-user-form__input-group"
                  label={t('COUNTRY')}
                  data={formValues.country}
                  type="select"
                  options={parsedCountries}
                  isRequired
                  disabled
                  onChange={handleValueChange}
                />
                <FormInput
                  id="county"
                  className="pp-user-form__input-group"
                  label={t('COUNTY')}
                  data={formValues.county}
                  type="select"
                  options={parsedCounties}
                  onChange={handleValueChange}
                />
              </div>
              <FormInput
                id="tocAccepted"
                className="pp-user-form__checkbox_input"
                data={formValues.tocAccepted}
                type="checkbox"
                onChange={handleValueChange}
                privacyPolicyUrl={privacyPolicyUrl}
                termsAndConditionsUrl={termsAndConditionsUrl}
              />
              {showSubmitButton && (
                <button className="pp-user-form__submit-button" type="submit" disabled={loading}>
                  {loading ? <LoadingComponent /> : t('REGISTER')}
                </button>
              )}
            </>
          )}
          {showErrorMessage && (
            <div className="pp-user-form error_message_wrapper">
              <div className="error_message_icon" />
              <div className="error_message">{showErrorMessage}</div>
            </div>
          )}
        </form>
      )}
      {showSuccessMessage && children}
    </>
  );
};

RegistrationForm.propTypes = {
  modifier: PropTypes.string,
  children: PropTypes.any,
  privacyPolicyUrl: PropTypes.string,
  termsAndConditionsUrl: PropTypes.string
};

RegistrationForm.defaultProps = {
  modifier: '',
  children: null,
  privacyPolicyUrl: null,
  termsAndConditionsUrl: null
};

export default RegistrationForm;
